import { CsI18n } from "../../shared/csI18n/csI18n";
import {
  Banner,
  Caption,
  Card,
  ColorPicker,
  DropZone,
  FormLayout,
  hsbToHex,
  Layout,
  ResourceItem,
  ResourceList,
  Select,
  Stack,
  TextField,
  Thumbnail,
  TextContainer,
} from "@shopify/polaris";
import HeadingHelper from "../../helpers/HeadingHelper";
import Switch from "react-switch/index";
import { CsValidation as V, CsValidationForm } from "../../shared/csValidationForm";
import ResourceListHelper from "../../helpers/ResourceListHelper";
import React, { useCallback, useState } from "react";

const ActiveCustomizationIcon = ({ cookieIconFile, handleChangeRaw }) => {
  const
    [rejectedFile, setRejectedFile] = useState(),
    handleDrop = useCallback((droppedFiles, acceptedFiles, rejectedFiles) => {
      // Only accept small file size
      if (acceptedFiles.length) {
        const file = acceptedFiles[0];
        if (file.size > 1024 * 1024) {
          rejectedFiles.push(file);
        } else {
          handleChangeRaw('cookieIconFile', file);
        }
      }
      setRejectedFile(rejectedFiles[0]);
    }, [handleChangeRaw]);

  const
    error = rejectedFile && (
      <Banner
        // todo: translation
        title={`"${rejectedFile.name}" could not be uploaded because it's too large or file type is not support (must be .png or .svg.)`}
        status="critical"
      >
      </Banner>
    ),
    fileUpload = !cookieIconFile && <DropZone.FileUpload />,
    uploadedFile = cookieIconFile && (
      <Stack alignment="center">
        <Thumbnail
          size="small"
          alt={cookieIconFile.name}
          source={window.URL.createObjectURL(cookieIconFile)}
        />
        <div>
          {cookieIconFile.name} <Caption>{cookieIconFile.size} bytes</Caption>
        </div>
      </Stack>
    )

  return (
    <Stack vertical>
      {error}
      <DropZone label={HeadingHelper.ltt('Custom icon', 'privacy.explain.cookies.icon')}
                allowMultiple={false} accept="image/png, image/svg+xml" type="image" onDrop={handleDrop}>
        {uploadedFile}
        {fileUpload}
      </DropZone>
    </Stack>
  );
}

const ActiveCustomizationColor = ({ cookieCustomization, handleChangeObjectState }) => {
  const
    colorHsb = cookieCustomization.color.hsb,
    handleChangeColor = useCallback(hsb => {
      handleChangeObjectState('cookieCustomization', {
        color: {
          hex: hsbToHex(hsb),
          hsb: hsb
        }
      });
    }, [handleChangeObjectState])

  return <ColorPicker color={colorHsb} onChange={handleChangeColor} />;
}

const ActiveCustomizationPreview = ({ cookieCustomization }) => {
  const
    iconUrl = cookieCustomization.enable && cookieCustomization.type === 'icon' && cookieCustomization.icon,
    colorHex = cookieCustomization.color.hex;

  const display = iconUrl ?
    <img src={iconUrl} className="custom-icon" alt="Cookie consent trigger" /> :
    <div size="48" className="Icon Icon__IconStyle-sc-1jpid70-0 dCVgIu svgr">
      <svg viewBox="0 0 24 24">
        <path fill={colorHex}
              d="M 12,0 C 5.372583,0 0,5.372583 0,12 c 0,6.627417 5.372583,12 12,12 6.627417,0 12,-5.372583 12,-12 0,-0.666667 -0.05333,-1.333333 -0.173333,-2.0000001 -0.36,-0.6666665 -1.160001,-0.6666665 -1.160001,-0.6666665 H 20 V 8 C 20,6.6666667 18.666666,6.6666667 18.666666,6.6666667 H 16 V 5.3333333 C 16,4 14.666667,4 14.666667,4 H 13.333333 V 1.3333333 C 13.333333,0 12,0 12,0 M 9.5,2 c 1.10457,-1e-7 2,0.8954305 2,2.0000001 0,1.1045695 -0.895431,2 -2,1.9999999 C 8.3954304,6.0000001 7.4999999,5.1045696 7.4999998,4.0000001 7.4999997,2.8954305 8.3954304,1.9999999 9.5,2 M 5,7.0000001 c 1.1045693,0 1.9999998,0.8954306 2,1.9999995 C 7,10.104569 6.1045695,11 5,11 3.8954305,11 3,10.104569 3,8.9999996 c 2e-7,-1.1045689 0.8954307,-1.9999995 2,-1.9999995 m 7,1.9999998 c 1.104569,0 2,0.8954312 2,2.0000001 0,1.104569 -0.895431,2 -2,2 -1.104569,0 -2,-0.895431 -2,-2 0,-1.1045689 0.895431,-2.0000001 2,-2.0000001 M 10,18 c 1.10457,0 2.000001,0.89543 2,2 0,1.10457 -0.895431,2 -2,2 v 0 C 8.895431,22 8,21.10457 8,20 v 0 c -1e-6,-1.104569 0.89543,-2 2,-2 M 6,13 c 1.1045691,0 1.9999992,0.89543 2.0000002,2 0,1.104568 -0.8954307,2 -2.0000002,2 C 4.8954304,17 3.9999998,16.104569 3.9999998,15 4.0000004,13.895431 4.8954308,13.000001 6,13 Z" />
        <path fill="#ffffff"
              d="m 13.505789,18.332856 c -0.22167,0.222858 -0.58233,0.222858 -0.804,0 h -10e-4 l -2.530004,-2.529 c -0.5211827,-0.536621 0.26709,-1.326845 0.805004,-0.807 l 2.128,2.127 6.186,-6.184999 c 0.535969,-0.482656 1.287658,0.269031 0.805,0.805 z m 2.427651,0.09851 c -0.484468,-0.001 -0.727727,-0.585594 -0.387,-0.93 l 6.047,-6.046998 c 0.21412,-0.212799 0.55988,-0.212799 0.774,0 0.2128,0.214119 0.2128,0.55988 0,0.773999 l -6.046,6.046999 c -0.103444,0.101525 -0.243069,0.157663 -0.388,0.156 z" />
      </svg>
    </div>;

  return (
    <TextContainer>
      <p><CsI18n>Preview</CsI18n></p>
      <button className="Common-ServicesButton" id="cs-cookies-consent-trigger">
        <div className="Common-ServicesIcon">
          <div size="48" className="Icon svgr">
            {display}
          </div>
        </div>
      </button>
    </TextContainer>
  );
}

const ActiveCustomization = ({ cookieCustomization, cookieIconFile, handleChangeRaw, handleChangeObjectState }) => {
  const
    predefinedTypes = [
      { label: CsI18n.t('Color'), value: 'color' },
      { label: CsI18n.t('Icon'), value: 'icon' },
    ],
    type = cookieCustomization.type,
    handleChangeType = useCallback(type => {
      handleChangeObjectState('cookieCustomization', { type });
    }, [handleChangeObjectState]);

  return (
    <>
      <FormLayout.Group>
        <FormLayout>
          <Select label={CsI18n.t('Type')} value={type} options={predefinedTypes} onChange={handleChangeType} />
          <ActiveCustomizationPreview cookieCustomization={cookieCustomization} />
        </FormLayout>

        <FormLayout>
          {type === 'color' ?
            <ActiveCustomizationColor
              cookieCustomization={cookieCustomization} handleChangeObjectState={handleChangeObjectState} /> :
            <ActiveCustomizationIcon cookieIconFile={cookieIconFile} handleChangeRaw={handleChangeRaw} />
          }
        </FormLayout>
      </FormLayout.Group>
    </>
  );
}

const Customization = ({ cookieCustomization, cookieIconFile, handleChangeRaw, handleChangeObjectState }) => {
  const
    enable = cookieCustomization.enable,
    handleSwitch = useCallback(status => {
      handleChangeObjectState('cookieCustomization', { enable: status });
    }, [handleChangeObjectState]);

  return (
    <>
      <FormLayout>
        <Switch
          aria-label={'Customize cookie consent'}
          className="react-switch"
          uncheckedIcon={false}
          checkedIcon={false}
          checked={enable}
          onChange={handleSwitch} />
      </FormLayout>

      {enable &&
      <ActiveCustomization
        cookieCustomization={cookieCustomization} cookieIconFile={cookieIconFile}
        handleChangeRaw={handleChangeRaw} handleChangeObjectState={handleChangeObjectState} />}
    </>
  );
}

const ActiveCookiesContent = (props) => {
  const {
    validatorClass,
    cookieTable,
    cookieCustomization, cookieIconFile, handleChangeObjectState,
    handleChangeRaw, handleArrayAdded, handleArrayChange, handleArrayRemoved,
  } = props;

  const renderCookieItem = (cookie, id, index) => {
    const name = 'cookieTable',
      readonly = cookie.readonly,
      doValidation = !!(cookie.name || cookie.link || cookie.purposes),  // Cast string to boolean
      validationRule = !doValidation ? '' : <V.Item rule="required" title={CsI18n.t('This field is required.')} />;
    return (
      <Card sectioned title={`${CsI18n.t('Item')}: ${index + 1}`}
            actions={ResourceListHelper.itemAction(name, index, handleArrayRemoved, readonly)}>
        <ResourceItem id={index}>
          <FormLayout>
            <FormLayout.Group>
              <V dynamicRules={true}>
                <TextField label={CsI18n.t('Cookie Editor')} value={cookie.name} readOnly={readonly}
                           onChange={handleArrayChange(name, index, 'name')} />
                {validationRule}
              </V>
              <V dynamicRules={true}>
                <TextField label={CsI18n.t('Link of Partner(s) privacy policies')} value={cookie.link}
                           readOnly={readonly} onChange={handleArrayChange(name, index, 'link')} />
                {validationRule}
              </V>
            </FormLayout.Group>
            <FormLayout.Group>
              <V dynamicRules={true}>
                <TextField label={CsI18n.t('Purposes')} value={cookie.purposes} readOnly={readonly}
                           onChange={handleArrayChange(name, index, 'purposes')} />
                {validationRule}
              </V>
            </FormLayout.Group>
          </FormLayout>
        </ResourceItem>
      </Card>
    );
  };

  return (
    <>
      <Card.Section title={CsI18n.t('Customization')}>
        <Customization
          cookieCustomization={cookieCustomization} cookieIconFile={cookieIconFile}
          handleChangeRaw={handleChangeRaw} handleChangeObjectState={handleChangeObjectState} />
      </Card.Section>

      <Card.Section title={CsI18n.t('Cookie items')}>
        <CsValidationForm name={validatorClass} realtime={true}>
          <ResourceList
            items={cookieTable}
            renderItem={renderCookieItem}
            alternateTool={ResourceListHelper.addNewAction('cookieTable', handleArrayAdded)}
          />
        </CsValidationForm>
      </Card.Section>
    </>
  );
}

const PrivacyCookies = (props) => {
  const {
    validatorClass,
    cookieActive, cookieTable, cookieCustomization, cookieIconFile,
    handleChangeRaw, handleChangeObjectState,
    handleArrayAdded, handleArrayChange, handleArrayRemoved,
  } = props;
  const handleActivate = useCallback(checked => {
    handleChangeRaw('cookieActive', checked);
  }, [handleChangeRaw]);

  return (
    <Layout.AnnotatedSection description={CsI18n.t("privacy.annotate.desc.cookies")}>
      <Card>
        <Card.Section title={<span
          className="card-section-title-tt">{HeadingHelper.ltt('Cookies', 'privacy.explain.cookies')}</span>}>
          <Switch
            className="react-switch"
            onChange={handleActivate}
            checked={cookieActive}
            uncheckedIcon={false}
            checkedIcon={false}
            aria-label={CsI18n.t("Cookies")}
          />
        </Card.Section>

        {cookieActive &&
        <ActiveCookiesContent
          validatorClass={validatorClass} cookieTable={cookieTable} cookieCustomization={cookieCustomization}
          cookieIconFile={cookieIconFile}
          handleChangeRaw={handleChangeRaw} handleChangeObjectState={handleChangeObjectState}
          handleArrayAdded={handleArrayAdded} handleArrayChange={handleArrayChange}
          handleArrayRemoved={handleArrayRemoved}
        />}
      </Card>
    </Layout.AnnotatedSection>
  );
};

export default PrivacyCookies;